import type { FaqSection } from "../types";

const INDUSTRIES: FaqSection[] = [
  {
    key: "construction",
    headline: "Frequently Asked Questions About Construction Accounting",
    header: "Answers That Help You Build with Confidence",
    description: [
      "Construction accounting comes with unique challenges across job costing, WIP reporting, retainage, and progress billing. These FAQs address how Orsak CPAs helps contractors track profitability by project, keep cash flow steady, and stay compliant with lender and bonding requirements.",
      "From Texas GCs to specialty trades, we set up systems that handle change orders, cost codes, and labor burdens so you can bid accurately and close out jobs with confidence.",
    ],
  },
  {
    key: "manufacturing",
    headline: "Frequently Asked Questions About Manufacturing Accounting",
    header: "Answers That Cut Through the Complexity",
    description: [
      "Manufacturing accounting involves more than basic bookkeeping — it’s about precise cost accounting, variance analysis, and inventory controls. These FAQs explain standard vs. actual costing, overhead allocation, and how Orsak CPAs helps improve margins with better visibility.",
      "Whether you operate in Houston or across Texas, we’ll help you streamline BOM tracking, work-in-process, and COGS reporting to support smarter pricing and production decisions.",
    ],
  },
  {
    key: "real-estate",
    headline: "Frequently Asked Questions About Real Estate Accounting",
    header: "Clarity for Complex Portfolios",
    description: [
      "Real estate requires clean entity structures, careful basis tracking, and thoughtful tax strategy across acquisitions, improvements, and dispositions. These FAQs cover depreciation methods, cost segregation, and passive activity rules.",
      "From single-asset LLCs to multi-state portfolios, Orsak CPAs supports investors, developers, and property managers with reporting that satisfies lenders and maximizes after-tax returns.",
    ],
  },
  {
    key: "technology",
    headline: "Frequently Asked Questions About Tech Company Accounting",
    header: "Built for Fast-Moving Startups and Scaling Tech Teams",
    description: [
      "Tech companies face rapid change: revenue recognition, SaaS metrics, R&D credits, equity comp, and investor reporting. These FAQs explain how Orsak CPAs supports founders and finance leaders with GAAP-aligned reporting and board-ready dashboards.",
      "Whether you’re pre-revenue or post-raise, we help you manage burn, model runway, and build the financial discipline needed to scale in Texas and beyond.",
    ],
  },
  {
    key: "transportation",
    headline: "Frequently Asked Questions About Transportation Accounting",
    header: "Clarity for Fast-Moving Fleet and Freight Operations",
    description: [
      "Transportation accounting brings unique tax rules, IFTA reporting, fuel and maintenance tracking, and equipment depreciation. These FAQs outline best practices for per-mile profitability and compliance timelines.",
      "From owner-operators to fleet managers, Orsak CPAs helps Texas carriers improve cash flow, evaluate equipment financing, and stay audit-ready year-round.",
    ],
  },
  {
    key: "oil-and-gas",
    headline: "Frequently Asked Questions About Oil & Gas Accounting",
    header: "Specialized Guidance for a High-Complexity Industry",
    description: [
      "Oil & gas finances involve working interests, royalty income, JIBs, depletion, and complex partnership reporting. These FAQs explain how Orsak CPAs navigates allocations, deductions, and basis tracking to protect owners and investors.",
      "Whether you manage mineral rights, participate in drilling programs, or operate upstream ventures, we deliver clear reporting and tax strategies aligned with Texas energy regulations.",
    ],
  },
  {
    key: "healthcare",
    headline: "Frequently Asked Questions About Healthcare Accounting",
    header: "Practical Answers for Busy Providers and Practice Owners",
    description: [
      "Healthcare practices juggle payer contracts, credentialing, AR cycles, and strict compliance. These FAQs cover revenue recognition for insurance reimbursements, payroll and benefits, and clean month-end closes.",
      "Orsak CPAs helps physicians, dentists, and clinics across Texas improve collections, manage overhead, and maintain accurate, audit-ready books.",
    ],
  },
  {
    key: "ranching-and-agribusiness",
    headline:
      "Frequently Asked Questions About Ranching & Agricultural Accounting",
    header: "Straightforward Guidance for a Complex, Seasonal Industry",
    description: [
      "Ranching and agribusiness require seasonal cash flow planning, inventory costing, and specialized tax rules for livestock, land, and equipment. These FAQs address depreciation, commodity income, and succession considerations.",
      "From family ranches to larger operations, Orsak CPAs helps Texas producers make tax-smart decisions and build durable, year-over-year profitability.",
    ],
  },
  {
    key: "professional",
    headline:
      "Frequently Asked Questions About Professional Service Firm Accounting",
    header: "Clarity for Client-Focused Businesses",
    description: [
      "Professional service firms thrive on clean timekeeping, WIP management, and predictable cash flow. These FAQs explain pricing models, partner distributions, and internal controls for law, consulting, architecture, and creative agencies.",
      "We help Texas firms build scalable systems for proposals, billing, and collections so owners can focus on delivery — not chasing invoices.",
    ],
  },
  {
    key: "nonprofits",
    headline: "Frequently Asked Questions About Nonprofit Accounting",
    header: "Answers for Mission-Driven Organizations",
    description: [
      "Nonprofits carry unique responsibilities: fund accounting, donor restrictions, grant reporting, and board-level transparency. These FAQs clarify the difference between restricted and unrestricted funds, budget controls, and audit preparedness.",
      "Orsak CPAs supports charities, churches, and associations across Texas with compliant reporting that builds trust and advances the mission.",
    ],
  },
];

const SERVICE_CATEGORIES: FaqSection[] = [
  {
    key: "accounting",
    headline: "Frequently Asked Questions About Accounting Services",
    header: "Get Clarity on Accounting Solutions",
    description: [
      "Our accounting FAQs cover everything from financial statement preparation to internal controls and compliance. Orsak CPAs is committed to simplifying complex reporting, helping you understand your numbers, and keeping your books accurate and audit-ready.",
      "Whether you need day-to-day bookkeeping support, GAAP implementation, or guidance on developing financial policies, these answers will help you see how professional accounting services strengthen your financial foundation.",
    ],
  },
  {
    key: "audit",
    headline: "Frequently Asked Questions About Audit Services",
    header: "Confidence Through Independent Assurance",
    description: [
      "Audits provide more than compliance — they build trust with lenders, investors, and stakeholders. In this FAQ, Orsak CPAs explains the difference between audits, reviews, and compilations, how we ensure GAAP compliance, and what documentation you'll need to prepare.",
      "From small businesses to complex organizations, our audit team delivers accurate, transparent reports that give you confidence in your financial statements.",
    ],
  },
  {
    key: "advisory",
    headline: "Frequently Asked Questions About Advisory Services",
    header: "Strategic Guidance for Smarter Decisions",
    description: [
      "Business decisions carry financial consequences, and our advisory services are designed to guide you with clarity and foresight. These FAQs address budgeting, forecasting, cash flow management, and succession planning — giving you insight into how Orsak CPAs helps you plan for long-term growth.",
      "Whether you're expanding operations, evaluating new opportunities, or preparing for ownership transition, our advisory support helps you make confident, informed choices.",
    ],
  },
  {
    key: "tax",
    headline: "Frequently Asked Questions About Tax Services",
    header: "Navigate Tax with Confidence",
    description: [
      "Taxes can be overwhelming, but with the right partner, you can approach every filing with clarity. In this FAQ section, Orsak CPAs answers common questions on tax planning, compliance, deductions, and strategies for minimizing liabilities while staying IRS-compliant.",
      "From individual returns to complex corporate structures, we help clients in Houston and across Texas file accurately and optimize their financial outcomes year-round.",
    ],
  },
  {
    key: "business",
    headline: "Frequently Asked Questions About Business Services",
    header: "Supporting Business Owners at Every Stage",
    description: [
      "Our business services are built to support entrepreneurs, family-owned companies, and corporations alike. These FAQs cover part-time CFO support, internal controls, financial system setup, and how Orsak CPAs partners with owners to streamline operations.",
      "Whether you're launching a startup or scaling an established company, we provide the expertise to help you manage growth, reduce risk, and position your business for success.",
    ],
  },
  {
    key: "individual",
    headline: "Frequently Asked Questions About Individual Services",
    header: "Personalized Support for Your Finances",
    description: [
      "Managing personal finances can be complex — from estate and trust planning to personal tax filings and financial statement preparation. This FAQ explains how Orsak CPAs supports individuals and families with tailored strategies that go beyond basic compliance.",
      "Our goal is to help you protect wealth, reduce tax burdens, and plan with confidence for every stage of life.",
    ],
  },
];

const SERVICE_ITEMS: FaqSection[] = [
  {
    key: "audit-of-financial-statements",
    headline: "Audit of Financial Statements FAQs",
    header: "Independent Assurance You Can Trust",
    description: [
      "An audit of financial statements provides the highest level of assurance for lenders, investors, and boards. Orsak CPAs delivers GAAP-compliant audits that strengthen stakeholder confidence and keep your business aligned with regulatory expectations.",
      "Based in Houston, Texas, our team works with businesses, nonprofits, and individuals to prepare clear, transparent reports. We help you identify risks, improve internal controls, and demonstrate financial integrity to those who matter most.",
    ],
  },
  {
    key: "accounting-system-selection-and-setup",
    headline: "Accounting System Selection & Setup FAQs",
    header: "Build the Right Foundation for Growth",
    description: [
      "Choosing and configuring the right accounting system is critical for accurate reporting and efficiency. Orsak CPAs helps evaluate platforms, migrate your data, and establish a chart of accounts tailored to your operations.",
      "From startups to established companies across Texas, we provide hands-on setup and training. Our goal is to ensure your system is audit-ready, scalable, and aligned with your business goals from day one.",
    ],
  },
  {
    key: "audits-reviews-and-financial-statements",
    headline: "Audits, Reviews, and Financial Statements FAQs",
    header: "Tailored Assurance for Every Need",
    description: [
      "Understanding the difference between audits, reviews, and compilations helps you choose the right level of assurance. Orsak CPAs guides clients through each option, balancing compliance requirements with cost-effectiveness.",
      "Serving Houston and Texas businesses, we prepare financial statements that satisfy stakeholders and support decision-making. Our team ensures accuracy, clarity, and compliance at every level.",
    ],
  },
  {
    key: "budgeting-and-forecasts",
    headline: "Budgeting & Forecasts FAQs",
    header: "Plan Ahead with Confidence",
    description: [
      "Accurate budgets and forecasts provide the roadmap for sustainable growth. Orsak CPAs helps businesses in Houston and across Texas build rolling forecasts, variance analyses, and scenario models that align with financial goals.",
      "With tailored insights, we equip you to anticipate challenges, manage cash flow, and make smarter investments. Our process keeps you proactive rather than reactive in every financial season.",
    ],
  },
  {
    key: "cash-flow-management",
    headline: "Cash Flow Management FAQs",
    header: "Keep Your Business Moving Forward",
    description: [
      "Cash flow management is vital for meeting payroll, paying vendors, and sustaining growth. Orsak CPAs helps you analyze AR/AP cycles, optimize payment timing, and build reserves that stabilize your business.",
      "For Houston and Texas companies, we create tailored cash strategies that reduce risk and prepare you for opportunities. Strong cash flow planning ensures you can handle both challenges and growth with confidence.",
    ],
  },
  {
    key: "compilation-of-financial-statements",
    headline: "Compilation of Financial Statements FAQs",
    header: "Simplified Reporting When You Need It",
    description: [
      "A compilation of financial statements provides organized, professional reports without the depth of a review or audit. Orsak CPAs helps clients prepare compilations that meet lender, investor, and board requirements.",
      "Based in Houston, Texas, we ensure your financials are clean, consistent, and easy to understand. Compilations are a cost-effective option when assurance isn’t required but professionalism is.",
    ],
  },
  {
    key: "financial-position-job-descriptions",
    headline: "Financial Position Job Descriptions FAQs",
    header: "Define Roles with Clarity and Confidence",
    description: [
      "Clear financial job descriptions support hiring, compliance, and internal controls. Orsak CPAs helps define responsibilities for bookkeepers, accountants, controllers, and CFOs within growing organizations.",
      "By aligning duties with business needs, we reduce risk and increase efficiency. Houston and Texas companies rely on us to strengthen their finance functions with practical, scalable role definitions.",
    ],
  },
  {
    key: "individual-accounting",
    headline: "Individual Accounting FAQs",
    header: "Personalized Care for Your Finances",
    description: [
      "Individual accounting goes beyond tax filings — it’s about building a complete financial picture. Orsak CPAs supports Houston and Texas families with personal financial statements, wealth planning, and compliance.",
      "Our approach helps you protect assets, reduce tax burdens, and prepare for future opportunities. With tailored support, you can make confident decisions about your financial life.",
    ],
  },
  {
    key: "policies-and-procedures",
    headline: "Policies & Procedures FAQs",
    header: "Strengthen Controls and Reduce Risk",
    description: [
      "Written policies and procedures create consistency, accountability, and audit readiness. Orsak CPAs helps organizations document financial processes such as cash management, payroll, and reconciliations.",
      "From Houston nonprofits to Texas corporations, our team ensures policies are practical, effective, and easy to implement. Strong procedures protect your business and improve efficiency.",
    ],
  },
  {
    key: "preparation-of-financial-statements",
    headline: "Preparation of Financial Statements FAQs",
    header: "Accurate Reporting for Smart Decisions",
    description: [
      "Financial statements are the foundation of every business decision. Orsak CPAs prepares GAAP-compliant balance sheets, income statements, and cash flow statements tailored to your needs.",
      "Serving businesses and individuals across Houston and Texas, we provide clarity that helps you meet lender requirements, satisfy boards, and confidently plan your next steps.",
    ],
  },
  {
    key: "software-selection-and-implementation",
    headline: "Software Selection & Implementation FAQs",
    header: "Technology That Fits Your Business",
    description: [
      "The right accounting and financial software can transform your workflows. Orsak CPAs helps Houston and Texas businesses select, configure, and integrate solutions that improve accuracy and save time.",
      "From QuickBooks to enterprise ERP systems, we provide guidance, data migration, and staff training. Our goal is to ensure your tools support growth, compliance, and audit readiness.",
    ],
  },
  {
    key: "quickbooks-services",
    headline: "QuickBooks Services FAQs",
    header: "Setup, Training, and Support Answered",
    description: [
      "QuickBooks is powerful when it's configured for how you actually work. These FAQs cover how Orsak CPAs helps Houston businesses set up QuickBooks Online or Desktop, clean up existing files, and train teams for accurate day-to-day bookkeeping.",
      "From chart-of-accounts design to ongoing support, we help you get reliable reporting out of QuickBooks so you can make confident decisions.",
    ],
  },
  {
    key: "disclosures-and-budget-support",
    headline: "Disclosures & Budget Support FAQs",
    header: "Transparent Reporting, Smarter Planning",
    description: [
      "Proper disclosures and accurate budgets give stakeholders the clarity they need. Orsak CPAs prepares footnotes, supplemental schedules, and forward-looking budgets that align with GAAP and board expectations.",
      "Based in Houston, Texas, we help organizations forecast with precision while meeting disclosure requirements. Our approach strengthens credibility and confidence with lenders, boards, and donors.",
    ],
  },
  {
    key: "gaap-implementation",
    headline: "GAAP Implementation FAQs",
    header: "Transition Smoothly to GAAP Standards",
    description: [
      "Adopting GAAP ensures compliance, transparency, and credibility. Orsak CPAs helps Houston and Texas businesses implement policies for revenue recognition, capitalization, and disclosures.",
      "We guide you through each step — from assessing gaps to training staff — so your financials meet professional standards and stand up to audits or investor scrutiny.",
    ],
  },
  {
    key: "medicare-cost-reports",
    headline: "Medicare Cost Reports FAQs",
    header: "Compliance for Healthcare Providers",
    description: [
      "Medicare cost reports are essential for providers seeking reimbursement. Orsak CPAs works with hospitals, clinics, and practices in Houston and across Texas to prepare accurate, audit-ready reports.",
      "Our team helps avoid common errors, improves documentation, and ensures timely filing. With expert guidance, you can protect reimbursements and focus on patient care.",
    ],
  },
  {
    key: "supplemental-schedules",
    headline: "Supplemental Schedules FAQs",
    header: "Detailed Insights Beyond the Financials",
    description: [
      "Supplemental schedules provide deeper visibility into revenue, expenses, and compliance metrics. Orsak CPAs prepares schedules that satisfy lenders, boards, and regulators.",
      "For businesses and nonprofits in Houston and Texas, we ensure supporting schedules are accurate, consistent, and aligned with your financial statements.",
    ],
  },
  {
    key: "succession-planning-and-wealth-transfer",
    headline: "Succession Planning & Wealth Transfer FAQs",
    header: "Protect What You’ve Built for the Next Generation",
    description: [
      "Business succession and wealth transfer require careful planning to minimize taxes and preserve value. Orsak CPAs works with Texas families and owners to design tax-efficient strategies for transitions.",
      "From valuations to trust coordination, our team helps ensure your legacy continues with confidence. We provide clarity for both family-owned and investor-backed businesses.",
    ],
  },
  {
    key: "internal-control-documentation",
    headline: "Internal Control Documentation FAQs",
    header: "Strengthen Your Processes with Documentation",
    description: [
      "Documented internal controls safeguard assets, reduce fraud risk, and ensure compliance. Orsak CPAs helps organizations map out critical financial workflows like disbursements, payroll, and reconciliations.",
      "Serving Houston and Texas businesses, we create practical documentation that satisfies auditors and empowers management to maintain effective oversight.",
    ],
  },
  {
    key: "internal-controls",
    headline: "Internal Controls FAQs",
    header: "Protect Your Business with Practical Controls",
    description: [
      "Internal controls protect assets, ensure accuracy, and support compliance. Orsak CPAs evaluates existing controls and recommends improvements tailored to your organization’s size and complexity.",
      "We help Houston and Texas clients strengthen segregation of duties, monitoring, and reporting. With the right controls, your business can reduce risk and build stakeholder trust.",
    ],
  },
  {
    key: "review-of-financial-statements",
    headline: "Review of Financial Statements FAQs",
    header: "Cost-Effective Assurance for Stakeholders",
    description: [
      "A review of financial statements provides limited assurance at a lower cost than a full audit. Orsak CPAs performs reviews that satisfy lenders and investors while saving you time and resources.",
      "For businesses across Houston and Texas, we ensure your financials are accurate, consistent, and compliant — without the depth of an audit when it’s not required.",
    ],
  },
  {
    key: "special-purpose-financial-statements",
    headline: "Special-Purpose Financial Statements FAQs",
    header: "Reporting Tailored to Your Needs",
    description: [
      "Special-purpose financial statements — such as cash-basis, tax-basis, or contractual — serve unique reporting requirements. Orsak CPAs prepares statements that align with lender, regulatory, or management needs.",
      "Serving Houston and Texas clients, we deliver clear, professional financials that balance compliance with practicality, ensuring stakeholders get the right information in the right format.",
    ],
  },
  {
    key: "part-time-controller-cfo",
    headline: "Part-Time Controller/CFO FAQs",
    header: "Senior Finance Leadership—Fraction of the Cost",
    description: [
      "Fractional Controller/CFO services give you strategic finance leadership without a full-time hire. Orsak CPAs helps Houston and Texas businesses improve reporting, forecasting, and KPI visibility while strengthening lender and investor confidence.",
      "We build scalable close processes, streamline cash management, and support board communications. Our part-time leaders align your finance function with growth goals and day-to-day operational needs.",
    ],
  },
  {
    key: "debt-covenant-compliance",
    headline: "Debt Covenant Compliance FAQs",
    header: "Stay in Good Standing with Your Lenders",
    description: [
      "Debt covenants require ongoing monitoring and timely reporting. Orsak CPAs tracks ratios, prepares calculations, and helps Houston and Texas borrowers avoid surprises during renewals or audits.",
      "We set up practical dashboards and month-end workflows so covenant checks are automatic, accurate, and audit-ready—keeping credit lines available and relationships strong.",
    ],
  },
  {
    key: "development-of-budgets",
    headline: "Development of Budgets FAQs",
    header: "Create Budgets that Actually Guide Decisions",
    description: [
      "Effective budgets turn strategy into measurable financial plans. Orsak CPAs partners with Texas leadership teams to build departmental budgets, cost drivers, and spending thresholds that reflect real-world constraints.",
      "We add variance analysis and review cadences so your team stays accountable. Houston businesses rely on our approach to forecast confidently and respond quickly to change.",
    ],
  },
  {
    key: "development-of-financial-policies-and-procedures",
    headline: "Development of Financial Policies & Procedures FAQs",
    header: "Documented Processes, Stronger Controls",
    description: [
      "Written policies and procedures reduce risk, improve consistency, and support audits. Orsak CPAs designs practical SOPs for disbursements, receipts, payroll, reconciliations, and month-end close.",
      "Serving Houston and Texas organizations, we tailor documentation to your size and systems—making compliance easier while strengthening internal controls and staff training.",
    ],
  },
  {
    key: "successful-planning",
    headline: "Successful Planning FAQs",
    header: "Turn Strategy into a Repeatable Planning Rhythm",
    description: [
      "Successful planning connects goals, budgets, and forecasts into a single operating cadence. Orsak CPAs helps Texas businesses establish quarterly reviews, KPI tracking, and scenario modeling to stay proactive.",
      "Our Houston team aligns leadership on priorities and cash needs so plans are actionable. The result is a durable rhythm that drives execution and long-term results.",
    ],
  },
  {
    key: "personal-financial-statements",
    headline: "Personal Financial Statements FAQs",
    header: "Lender-Ready Personal Financials",
    description: [
      "Individuals often need formal personal financial statements for loans, investments, or estate planning. Orsak CPAs prepares accurate net worth statements, income summaries, and supporting schedules for Texas clients.",
      "We ensure valuations and documentation meet lender expectations and timelines. Our Houston team delivers professional reports that make applications smoother and decisions faster.",
    ],
  },
  {
    key: "estate-and-trusts",
    headline: "Estate & Trusts FAQs",
    header: "Tax-Smart Estate & Trust Administration",
    description: [
      "Estate and trust matters require careful coordination across tax filings, fiduciary accounting, and distributions. Orsak CPAs supports Texas families and trustees with compliant, timely reporting.",
      "We work with your attorneys and advisors to minimize taxes, document basis, and preserve wealth. Our Houston-based team brings clarity to complex estate and trust requirements.",
    ],
  },
  {
    key: "federal-tax-return-preparation",
    headline: "Federal Tax Return Preparation FAQs",
    header: "Accurate Returns, Fewer Surprises",
    description: [
      "From entity selection to elections and credits, federal tax filings demand precision. Orsak CPAs prepares accurate, IRS-compliant returns for businesses, nonprofits, and individuals across Texas.",
      "We organize documentation, identify savings opportunities, and plan for year-round tax efficiency. Houston clients trust our process to minimize liabilities and reduce audit risk.",
    ],
  },
  {
    key: "state-franchise-income-and-tax-return",
    headline: "State Franchise, Income, & Tax Return FAQs",
    header: "Multi-State & Texas Franchise Compliance Made Simple",
    description: [
      "State filings can be complex—especially with Texas franchise tax and multi-state apportionment. Orsak CPAs manages registrations, filings, and notices so you remain compliant across jurisdictions.",
      "Our Houston team streamlines data gathering and deadlines while optimizing state tax positions. We keep you organized and ahead of state-level changes that affect your business.",
    ],
  },
  {
    key: "tax-compliance",
    headline: "Tax Compliance FAQs",
    header: "Year-Round Compliance Without the Headaches",
    description: [
      "Tax compliance involves deadlines, estimates, extensions, and documentation control. Orsak CPAs builds a calendar-driven process for Houston and Texas clients to file accurately and on time.",
      "We coordinate with your bookkeepers and systems to reduce friction and avoid penalties. Our proactive approach keeps you audit-ready while freeing your team to focus on operations.",
    ],
  },
  {
    key: "tax-planning",
    headline: "Tax Planning FAQs",
    header: "Proactive Strategies to Reduce Your Tax Burden",
    description: [
      "Tax planning isn’t a once-a-year task—it’s a year-round strategy. Orsak CPAs helps Texas businesses and individuals time income and deductions, evaluate entities, and leverage credits.",
      "Based in Houston, we align tax strategies with your goals—cash flow, investments, and growth—so you keep more of what you earn while staying fully compliant.",
    ],
  },
  {
    key: "oil-and-gas-royalty-tax",
    headline: "Oil & Gas Royalty Owner Tax FAQs",
    header: "Answers for Mineral and Royalty Owners",
    description: [
      "Royalty income, division orders, and depletion raise questions most general preparers can’t answer. These FAQs cover how depletion works, the difference between royalty and working interests, and what your K-1s and 1099s actually mean.",
      "Orsak CPAs helps Texas owners report mineral and royalty income correctly while capturing every deduction the rules allow.",
    ],
  },
  {
    key: "ranch-and-landowner-tax",
    headline: "Ranch, Farm & Landowner Tax FAQs",
    header: "Answers for Texas Land and Mineral Owners",
    description: [
      "Owning Texas land raises questions about agricultural valuation, rollback taxes, mineral income, and passing property to the next generation. These FAQs explain how each works and where owners most often overpay.",
      "Orsak CPAs helps ranchers, farmers, and landowners plan ahead so valuations hold and transfers stay tax-efficient.",
    ],
  },
  {
    key: "real-estate-investor-tax",
    headline: "Real Estate Investor Tax FAQs",
    header: "Answers on 1031s, Depreciation, and REP Status",
    description: [
      "Real estate offers some of the tax code’s best tools—and its trickiest rules. These FAQs cover 1031 exchanges, cost segregation, real estate professional status, and the passive-activity limits that catch many investors.",
      "Orsak CPAs helps Houston investors structure deals and depreciation to lower their effective tax rate.",
    ],
  },
  {
    key: "business-sale-exit",
    headline: "Business Sale & Exit Planning FAQs",
    header: "Answers for Owners Preparing to Sell",
    description: [
      "Selling a business is often a once-in-a-lifetime event with lasting tax consequences. These FAQs cover QSBS, installment sales, asset-versus-stock deals, and why timing your planning before the LOI matters.",
      "Orsak CPAs helps owners structure the sale so more of the proceeds stay with you.",
    ],
  },
];

export const FAQ_SECTIONS: FaqSection[] = [
  {
    key: "",
    headline: "Frequently Asked Questions About Orsak CPAs",
    header: "Answers to Common Client Questions",
    description: [
      "Choosing the right CPA firm is a critical decision, and we know you’ll have questions. At Orsak CPAs, our landing page FAQ section addresses what makes us different, how we work with clients in Houston and across Texas, and what you can expect from our team.",
      "From understanding our range of services to how we handle remote and local clients, these FAQs provide clarity. We combine technical expertise with proactive insight to help you move forward with confidence.",
    ],
  },
  {
    key: "services",
    headline: "Frequently Asked Questions About Our CPA Services",
    header: "Clarity on How We Deliver Support",
    description: [
      "Our services page FAQ section helps you understand which solutions fit your specific needs — whether that’s audit assurance, tax planning, or financial system setup. We outline how services can be combined and scaled as your business grows.",
      "Based in Houston, Texas, Orsak CPAs offers both one-time engagements and ongoing support. These FAQs provide insight into our process, timelines, and how we build customized service packages for individuals, businesses, and nonprofits.",
    ],
  },
  {
    key: "industries",
    headline: "Frequently Asked Questions About the Industries We Serve",
    header: "Specialized Expertise Across Sectors",
    description: [
      "Each industry carries unique financial, tax, and compliance requirements. Our industries page FAQ section highlights how Orsak CPAs tailors support for sectors like construction, healthcare, oil & gas, technology, real estate, and nonprofits across Houston and Texas.",
      "From industry-specific reporting to state and federal compliance, these FAQs demonstrate how our knowledge of sector challenges ensures proactive solutions and audit-ready financials for our clients.",
    ],
  },
  {
    key: "irs-resolution",
    headline: "Frequently Asked Questions About IRS Tax Resolution",
    header: "Answers When You're Facing the IRS",
    description: [
      "IRS problems are stressful, but you have options. These FAQs explain how Orsak CPAs helps Houston, Texas taxpayers resolve back taxes, stop collection actions, and negotiate with the IRS through payment plans and offers in compromise.",
      "From unfiled returns to liens, levies, and wage garnishment, we represent you before the IRS and work toward the best resolution available for your situation.",
    ],
  },
  ...INDUSTRIES,
  ...SERVICE_CATEGORIES,
  ...SERVICE_ITEMS,
];

export const FAQ_SECTIONS_BY_KEY = Object.fromEntries(
  FAQ_SECTIONS.map(faq => [faq.key, faq]),
);
